import {
  type GetTimeSeriesViewRequest,
  leaderboardClient,
} from '@neptune/shared/core-apis-leaderboard-domain';

import { VegaLiteDataLimitError, VegaLiteDataProtocolError } from './vega-lite-data-error';
import type {
  FloatNonFinite,
  FloatViewBucket,
  FloatViewPoint,
} from './vega-lite-float-series-types';
import { formatVegaLiteLimit, VEGA_LITE_FLOAT_SERIES_LIMITS } from './vega-lite-widget-limits';

export type FloatViewRequest = GetTimeSeriesViewRequest['request'];
export type FloatViewResponse = ReadonlyMap<string, readonly FloatViewPoint[]>;
export type FloatViewApi = {
  getView(
    request: FloatViewRequest,
    signal: AbortSignal,
    maxPoints?: number,
  ): Promise<FloatViewResponse>;
};
type FloatViewClient = {
  getTimeSeriesView(request: GetTimeSeriesViewRequest, init?: RequestInit): Promise<unknown>;
};

function record(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new VegaLiteDataProtocolError(`${path} must be an object.`);
  }

  return { ...value };
}

function finiteOrNull(value: unknown, path: string): number | null {
  if (value === undefined || value === null || nonFiniteMarker(value)) {
    return null;
  }

  if (typeof value !== 'number') {
    throw new VegaLiteDataProtocolError(`${path} must be numeric, null or a non-finite marker.`);
  }

  return Number.isFinite(value) ? value : null;
}

function nonFiniteMarker(value: unknown): FloatNonFinite | null {
  if (value === 'NaN' || (typeof value === 'number' && Number.isNaN(value))) {
    return 'NaN';
  }

  if (value === 'Infinity' || value === Infinity) {
    return 'Infinity';
  }

  if (value === '-Infinity' || value === -Infinity) {
    return '-Infinity';
  }

  return null;
}

function integer(value: unknown, path: string, nonNegative = true): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || (nonNegative && value < 0)) {
    throw new VegaLiteDataProtocolError(
      `${path} must be a ${nonNegative ? 'non-negative ' : ''}safe integer.`,
    );
  }

  return value;
}

function normalizeBucket(value: unknown, path: string): FloatViewBucket | null {
  if (value === undefined || value === null) {
    return null;
  }

  const bucket = record(value, path);
  const count = integer(bucket.count, `${path}.count`);
  const minY = finiteOrNull(bucket.minY, `${path}.minY`);
  const maxY = finiteOrNull(bucket.maxY, `${path}.maxY`);
  const sum = finiteOrNull(bucket.sum, `${path}.sum`);
  return {
    bucketNo: integer(bucket.bucketNo, `${path}.bucketNo`, false),
    minY: count === 0 ? null : minY,
    maxY: count === 0 ? null : maxY,
    sum: count === 0 ? null : sum,
    count,
    positiveInfCount: integer(bucket.positiveInfCount ?? 0, `${path}.positiveInfCount`),
    negativeInfCount: integer(bucket.negativeInfCount ?? 0, `${path}.negativeInfCount`),
    nanCount: integer(bucket.nanCount ?? 0, `${path}.nanCount`),
  };
}

function normalizePoint(value: unknown, path: string): FloatViewPoint {
  const point = record(value, path);

  if (typeof point.x !== 'number' || !Number.isFinite(point.x)) {
    throw new VegaLiteDataProtocolError(
      `${path}.x must be finite; invalid X cannot fall back to step.`,
    );
  }

  if (typeof point.interpolation !== 'boolean') {
    throw new VegaLiteDataProtocolError(`${path}.interpolation must be a boolean.`);
  }

  return {
    x: point.x,
    pointY: finiteOrNull(point.y, `${path}.y`),
    pointNonFinite: nonFiniteMarker(point.y),
    step: finiteOrNull(point.step, `${path}.step`),
    timestampMillis: finiteOrNull(point.timestampMillis, `${path}.timestampMillis`),
    interpolation: point.interpolation,
    bucket: normalizeBucket(point.bucket, `${path}.bucket`),
  };
}

export function normalizeFloatViewResponse(
  response: unknown,
  expectedIds: readonly string[],
  maxPoints: number = VEGA_LITE_FLOAT_SERIES_LIMITS.maxPoints,
): FloatViewResponse {
  const value = record(response, 'Float-series response');

  if (!Array.isArray(value.customSeries)) {
    throw new VegaLiteDataProtocolError('Float-series response must contain customSeries.');
  }

  if (new Set(expectedIds).size !== expectedIds.length) {
    throw new VegaLiteDataProtocolError('The float-series request contains duplicate custom IDs.');
  }

  const expected = new Set(expectedIds);
  const series = new Map<string, FloatViewPoint[]>();
  let pointCount = 0;
  value.customSeries.forEach((entry: unknown) => {
    const item = record(entry, 'customSeries entry');

    if (
      typeof item.customId !== 'string' ||
      !expected.has(item.customId) ||
      series.has(item.customId)
    ) {
      throw new VegaLiteDataProtocolError(
        'Float-series response contains an unexpected or duplicate custom ID.',
      );
    }

    if (!Array.isArray(item.values)) {
      throw new VegaLiteDataProtocolError(
        `Float series ${item.customId} must contain a values array.`,
      );
    }

    pointCount += item.values.length;

    if (pointCount > maxPoints) {
      throw new VegaLiteDataLimitError(
        `Float-series response exceeds the ${formatVegaLiteLimit(maxPoints)}-point safety limit.`,
      );
    }

    const xs = new Set<number>();
    const points = item.values.map((point: unknown, index) => {
      const normalized = normalizePoint(point, `customSeries[${item.customId}].values[${index}]`);

      if (xs.has(normalized.x)) {
        throw new VegaLiteDataProtocolError(
          `Duplicate returned X ${normalized.x} in ${item.customId}.`,
        );
      }

      xs.add(normalized.x);
      return normalized;
    });
    series.set(item.customId, points);
  });

  if (series.size !== expected.size) {
    throw new VegaLiteDataProtocolError(
      `Missing float-series response IDs: ${expectedIds.filter((id) => !series.has(id)).join(', ')}.`,
    );
  }

  return series;
}

export function createFloatViewApi(client: FloatViewClient = leaderboardClient): FloatViewApi {
  return {
    async getView(request, signal, maxPoints) {
      const response = await client.getTimeSeriesView({ request }, { signal });
      return normalizeFloatViewResponse(
        response,
        request.metrics?.map((metric) => metric.customId) ?? [],
        maxPoints,
      );
    },
  };
}

export const floatViewApi = createFloatViewApi();
